import { Asset, AssetType, VariantResource } from "../../types";

/**
 * A wrapping component that provides a simple interface for interacting with a variant.
 */
export class Variant {
    private readonly variantData: VariantResource;

    constructor(variant: VariantResource) {
        this.variantData = variant;
    }

    getType(): AssetType | undefined {
        return this.variantData.asset?.type;
    }

    /**
     * @returns The unique identifier for the variant.
     */
    getId(): string {
        return this.variantData.id!;
    }

    /**
     * @returns The configured name of the variant. Generally a human readable value.
     */
    getName(): string {
        return this.variantData.name;
    }

    /**
     * @returns The price modifier for this variant. This is the amount that will be added to the base price of the product.
     */
    getPriceFormatted(locales?: Intl.LocalesArgument, options?: Intl.NumberFormatOptions | undefined): string {
        return this.variantData.priceModifier.toLocaleString(locales ? locales : "en-US", {
            style: "currency",
            currency: "USD",
            ...options,
        });
    }

    /**
     * @returns The price modifier for this variant. This is the amount that will be added to the base price of the product.
     *          Presented in subunits of the currency of the option. For example, if the option is configured to use USD, the price modifier will be in cents.
     */
    getPrice(): number {
        return this.variantData.priceModifier;
    }

    /**
     * @returns The URL for the base asset resource configured on this variant
     */
    getAsset(): string | undefined {
        return this.variantData.asset?.versions?.find((v) => v.name === "cdn")?.link;
    }

    /**
     * @returns The URL for the base asset resource configured on this variant
     */
    getAssetResource(): Asset | undefined {
        return this.variantData.asset;
    }

    /**
     * @returns The URL for a thumbnail resource configured on this variant.
     */
    getThumbnail(): string | undefined {
        // Thumbnails are uploaded high res. We generate thumbnails internally. First we look
        // for this internally generated thumbnail. If it doesn't exist, we fall back to the original
        // thumbnail asset (Which is assumably going to be small anyway...)
        // NOTE: Certain assets such as SVG don't currently generate a thumb.
        const thumb = this.variantData.thumbnail?.versions?.find((v) => v.name === "thumbnail")?.link;
        const full = this.variantData.thumbnail?.fileLink;
        return thumb || full;
    }

    /**
     * @returns When this variant is configured to have a color, this will return the hex value for that color.
     */
    getColor(): string | undefined {
        return this.variantData.color;
    }

    /**
     * @returns True when the variant is the default for its containing option. False otherwise.
     */
    isDefault(): boolean {
        return !!this.variantData.default;
    }

    /**
     * @returns The underlying variant resource. Generally not needed but made available just incase.
     */
    getResource(): VariantResource {
        return this.variantData;
    }

    /**
     * @returns True when the variant is enabled. False otherwise.
     */
    isEnabled(): boolean {
        return this.variantData.enabled;
    }
}
